/**
 * Unit Detail Page
 * Individual unit management with service history and maintenance scheduling
 * Track service requests, maintenance schedules, and unit-specific analytics
 */

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { AdminPageLayout, StatsGrid } from '@/components/layout/AdminPageLayout';
import { StatCard } from '@/components/layout/AdminStatCard';
import { Card } from '@/components/ui/card';
import { Home, Calendar, Wrench, DollarSign, User, Phone, Mail, Activity } from 'lucide-react';

interface Unit {
  id: string;
  unit_number: string;
  building_id: string;
  square_footage: number;
  bedrooms: number;
  bathrooms: number;
  monthly_fee: number;
  owner_name: string;
  owner_email: string;
  owner_phone: string;
  status: string;
  lease_start?: string;
  lease_end?: string;
  notes?: string;
}

interface ServiceRequest {
  id: string;
  unit_id: string;
  request_type: string;
  description: string;
  status: string;
  priority: string;
  created_at: string;
  scheduled_date?: string;
  completed_date?: string;
  technician_notes?: string;
}

interface MaintenanceSchedule {
  id: string;
  unit_id: string;
  maintenance_type: string;
  frequency: string;
  last_service?: string;
  next_service?: string;
  status: string;
  notes?: string;
}

export default function UnitDetailPage() {
  const { unitId } = useParams<{ unitId: string }>();
  const [unit, setUnit] = useState<Unit | null>(null);
  const [serviceRequests, setServiceRequests] = useState<ServiceRequest[]>([]);
  const [maintenanceSchedule, setMaintenanceSchedule] = useState<MaintenanceSchedule[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'service' | 'maintenance' | 'history'>('overview');

  useEffect(() => {
    if (unitId) {
      loadUnitData();
    }
  }, [unitId]);

  const loadUnitData = async () => {
    setLoading(true);

    // Load unit details
    const { data: unitData } = await supabase
      .from('units')
      .select('*')
      .eq('id', unitId)
      .single();

    // Load service requests
    const { data: serviceData } = await supabase
      .from('service_requests')
      .select('*')
      .eq('unit_id', unitId)
      .order('created_at', { ascending: false });

    // Load maintenance schedule
    const { data: maintenanceData } = await supabase
      .from('maintenance_schedule')
      .select('*')
      .eq('unit_id', unitId)
      .order('next_service', { ascending: true });

    setUnit(unitData);
    setServiceRequests(serviceData || []);
    setMaintenanceSchedule(maintenanceData || []);
    setLoading(false);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'occupied': return 'bg-green-100 text-green-800';
      case 'vacant': return 'bg-gray-100 text-gray-600';
      case 'maintenance': return 'bg-yellow-100 text-yellow-800';
      case 'reserved': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  const getServiceStatusColor = (status: string) => {
    switch (status) {
      case 'new': return 'bg-blue-100 text-blue-800';
      case 'assigned': return 'bg-purple-100 text-purple-800';
      case 'in_progress': return 'bg-yellow-100 text-yellow-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-gray-100 text-gray-600';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  if (loading) {
    return (
      <AdminPageLayout title="Unit Details" subtitle="Loading...">
        <div className="py-8 text-center text-gray-500">Loading unit information...</div>
      </AdminPageLayout>
    );
  }

  if (!unit) {
    return (
      <AdminPageLayout title="Unit Not Found" subtitle="The requested unit could not be found">
        <div className="py-8 text-center text-gray-500">Unit not found</div>
      </AdminPageLayout>
    );
  }

  return (
    <AdminPageLayout
      title={`Unit ${unit.unit_number}`}
      subtitle={`${unit.bedrooms} bed, ${unit.bathrooms} bath • ${unit.square_footage?.toLocaleString()} sq ft`}
      actions={
        <div className="flex gap-2">
          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium">
            New Service Request
          </button>
          <button className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium">
            Schedule Maintenance
          </button>
        </div>
      }
    >
      {/* Unit Overview Cards */}
      <StatsGrid cols={4}>
        <StatCard
          label="Monthly Fee"
          value={`$${unit.monthly_fee}`}
          icon={DollarSign}
          subtitle="rent amount"
        />
        <StatCard
          label="Status"
          value={unit.status}
          icon={Home}
          subtitle={`since ${unit.lease_start ? new Date(unit.lease_start).toLocaleDateString() : 'unknown'}`}
        />
        <StatCard
          label="Open Requests"
          value={serviceRequests.filter(sr => sr.status !== 'completed' && sr.status !== 'cancelled').length}
          icon={Wrench}
          subtitle={`${serviceRequests.filter(sr => sr.status === 'completed').length} completed`}
        />
        <StatCard
          label="Upcoming Maintenance"
          value={maintenanceSchedule.filter(ms => ms.next_service && new Date(ms.next_service) > new Date()).length}
          icon={Calendar}
          subtitle="scheduled items"
        />
      </StatsGrid>

      {/* Tab Navigation */}
      <div className="flex space-x-1 mb-6">
        {[
          { id: 'overview', label: 'Overview', icon: Home },
          { id: 'service', label: 'Service Requests', icon: Wrench },
          { id: 'maintenance', label: 'Maintenance', icon: Calendar },
          { id: 'history', label: 'History', icon: Activity }
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as any)}
            className={`px-4 py-2 rounded-lg font-medium flex items-center gap-2 ${
              activeTab === tab.id
                ? 'bg-blue-100 text-blue-700 border border-blue-200'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Unit Information */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Home className="w-5 h-5" />
              Unit Information
            </h3>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-600">Unit Number</label>
                  <p className="text-lg font-semibold text-gray-900">{unit.unit_number}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Status</label>
                  <span className={`inline-flex px-2 py-1 text-sm font-medium rounded-full ${getStatusColor(unit.status)}`}>
                    {unit.status}
                  </span>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Bedrooms</label>
                  <p className="text-lg font-semibold text-gray-900">{unit.bedrooms}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Bathrooms</label>
                  <p className="text-lg font-semibold text-gray-900">{unit.bathrooms}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Square Footage</label>
                  <p className="text-lg font-semibold text-gray-900">{unit.square_footage?.toLocaleString()} sq ft</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Monthly Fee</label>
                  <p className="text-lg font-semibold text-gray-900">${unit.monthly_fee}</p>
                </div>
              </div>

              {unit.lease_start && (
                <div className="pt-4 border-t">
                  <label className="text-sm font-medium text-gray-600">Lease Period</label>
                  <p className="text-sm text-gray-900">
                    {new Date(unit.lease_start).toLocaleDateString()} -
                    {unit.lease_end ? new Date(unit.lease_end).toLocaleDateString() : ' Ongoing'}
                  </p>
                </div>
              )}

              {unit.notes && (
                <div className="pt-4 border-t">
                  <label className="text-sm font-medium text-gray-600">Notes</label>
                  <p className="text-sm text-gray-900 mt-1">{unit.notes}</p>
                </div>
              )}
            </div>
          </Card>

          {/* Owner Information */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <User className="w-5 h-5" />
              Owner/Tenant Information
            </h3>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-600">Name</label>
                <p className="text-lg font-semibold text-gray-900">{unit.owner_name}</p>
              </div>
              <div className="grid grid-cols-1 gap-4">
                <div className="flex items-center gap-3">
                  <Mail className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-900">{unit.owner_email}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Phone className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-900">{unit.owner_phone}</span>
                </div>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Service Requests Tab */}
      {activeTab === 'service' && (
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Service Requests</h3>
          {serviceRequests.length === 0 ? (
            <div className="py-8 text-center text-gray-400">
              No service requests found for this unit.
            </div>
          ) : (
            <div className="space-y-4">
              {serviceRequests.map((request) => (
                <div key={request.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="font-semibold text-gray-900">{request.request_type}</h4>
                      <p className="text-sm text-gray-600 mt-1">{request.description}</p>
                    </div>
                    <div className="flex gap-2">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getServiceStatusColor(request.status)}`}>
                        {request.status}
                      </span>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getPriorityColor(request.priority)}`}>
                        {request.priority}
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-600">
                    <div>
                      <span className="font-medium">Created:</span>
                      <p>{new Date(request.created_at).toLocaleDateString()}</p>
                    </div>
                    {request.scheduled_date && (
                      <div>
                        <span className="font-medium">Scheduled:</span>
                        <p>{new Date(request.scheduled_date).toLocaleDateString()}</p>
                      </div>
                    )}
                    {request.completed_date && (
                      <div>
                        <span className="font-medium">Completed:</span>
                        <p>{new Date(request.completed_date).toLocaleDateString()}</p>
                      </div>
                    )}
                    <div className="md:col-span-1">
                      <button className="text-blue-600 hover:text-blue-800 font-medium">
                        View Details
                      </button>
                    </div>
                  </div>

                  {request.technician_notes && (
                    <div className="mt-3 pt-3 border-t">
                      <span className="font-medium text-sm text-gray-600">Technician Notes:</span>
                      <p className="text-sm text-gray-900 mt-1">{request.technician_notes}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {/* Maintenance Tab */}
      {activeTab === 'maintenance' && (
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Maintenance Schedule</h3>
          {maintenanceSchedule.length === 0 ? (
            <div className="py-8 text-center text-gray-400">
              No maintenance schedule found for this unit.
            </div>
          ) : (
            <div className="space-y-4">
              {maintenanceSchedule.map((item) => (
                <div key={item.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="font-semibold text-gray-900">{item.maintenance_type}</h4>
                      <p className="text-sm text-gray-600">Frequency: {item.frequency}</p>
                    </div>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(item.status)}`}>
                      {item.status}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm text-gray-600">
                    {item.last_service && (
                      <div>
                        <span className="font-medium">Last Service:</span>
                        <p>{new Date(item.last_service).toLocaleDateString()}</p>
                      </div>
                    )}
                    {item.next_service && (
                      <div>
                        <span className="font-medium">Next Service:</span>
                        <p className={new Date(item.next_service) <= new Date() ? 'text-red-600 font-medium' : ''}>
                          {new Date(item.next_service).toLocaleDateString()}
                        </p>
                      </div>
                    )}
                    <div className="md:col-span-1">
                      <button className="text-blue-600 hover:text-blue-800 font-medium">
                        Schedule
                      </button>
                    </div>
                  </div>

                  {item.notes && (
                    <div className="mt-3 pt-3 border-t">
                      <span className="font-medium text-sm text-gray-600">Notes:</span>
                      <p className="text-sm text-gray-900 mt-1">{item.notes}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Service History</h3>
          <div className="space-y-4">
            {/* This would show a timeline of all activities */}
            <div className="py-8 text-center text-gray-400">
              Service history timeline would be displayed here.
              <br />
              <small className="text-gray-300">Integration with service tracking system needed.</small>
            </div>
          </div>
        </Card>
      )}
    </AdminPageLayout>
  );
}
